/**
 * Runtime bundle analysis utilities
 */

interface ChunkInfo {
  name: string
  size: number
  loadTime: number
  timestamp: number
}

interface BundleMetrics {
  totalChunks: number
  totalSize: number
  averageLoadTime: number
  largestChunk: ChunkInfo | null
  slowestChunk: ChunkInfo | null
}

class BundleAnalyzer {
  private chunks: Map<string, ChunkInfo> = new Map()
  private observer: PerformanceObserver | null = null

  constructor() {
    this.initializeObserver()
  }

  private initializeObserver() {
    if (typeof window !== 'undefined' && 'PerformanceObserver' in window) {
      this.observer = new PerformanceObserver((list) => {
        const entries = list.getEntries()
        entries.forEach((entry) => {
          if (entry.entryType === 'resource' && entry.name.includes('.js')) {
            this.trackChunkLoad(entry as PerformanceResourceTiming)
          }
        })
      })

      this.observer.observe({ entryTypes: ['resource'] })
    }
  }

  private trackChunkLoad(entry: PerformanceResourceTiming) {
    const chunkName = this.extractChunkName(entry.name)
    const loadTime = entry.responseEnd - entry.requestStart
    const size = this.estimateSize(entry)

    const chunkInfo: ChunkInfo = {
      name: chunkName,
      size,
      loadTime,
      timestamp: Date.now(),
    }

    this.chunks.set(chunkName, chunkInfo)

    if (process.env.NODE_ENV === 'development') {
      console.log(`📦 Chunk loaded: ${chunkName} (${size}KB in ${loadTime.toFixed(2)}ms)`)
    }
  }

  private extractChunkName(url: string): string {
    const parts = url.split('/')
    const filename = parts[parts.length - 1]
    return filename.replace(/\.[a-f0-9]+\.js$/, '.js') // Remove hash
  }

  private estimateSize(entry: PerformanceResourceTiming): number {
    // Rough estimation based on transfer time and typical compression ratios
    const transferTime = entry.responseEnd - entry.responseStart
    const estimatedBytes = transferTime * 50 // Very rough estimate
    return Math.round(estimatedBytes / 1024) // Convert to KB
  }

  getMetrics(): BundleMetrics {
    const chunks = Array.from(this.chunks.values())
    
    if (chunks.length === 0) {
      return {
        totalChunks: 0,
        totalSize: 0,
        averageLoadTime: 0,
        largestChunk: null,
        slowestChunk: null,
      }
    }

    const totalSize = chunks.reduce((sum, chunk) => sum + chunk.size, 0)
    const averageLoadTime = chunks.reduce((sum, chunk) => sum + chunk.loadTime, 0) / chunks.length
    const largestChunk = chunks.reduce((largest, chunk) => 
      chunk.size > (largest?.size || 0) ? chunk : largest
    )
    const slowestChunk = chunks.reduce((slowest, chunk) => 
      chunk.loadTime > (slowest?.loadTime || 0) ? chunk : slowest
    )

    return {
      totalChunks: chunks.length,
      totalSize,
      averageLoadTime,
      largestChunk,
      slowestChunk,
    }
  }

  getRecommendations(): string[] {
    const metrics = this.getMetrics()
    const recommendations: string[] = []

    if (metrics.largestChunk && metrics.largestChunk.size > 500) {
      recommendations.push(`Consider splitting large chunk: ${metrics.largestChunk.name} (${metrics.largestChunk.size}KB)`)
    }

    if (metrics.slowestChunk && metrics.slowestChunk.loadTime > 1000) {
      recommendations.push(`Slow loading chunk detected: ${metrics.slowestChunk.name} (${metrics.slowestChunk.loadTime.toFixed(2)}ms)`)
    }

    if (metrics.totalSize > 2000) {
      recommendations.push('Total bundle size is large. Consider more aggressive code splitting.')
    }

    if (metrics.totalChunks > 20) {
      recommendations.push('Many small chunks detected. Consider consolidating some chunks.')
    }

    if (recommendations.length === 0) {
      recommendations.push('Bundle performance looks good! 🎉')
    }

    return recommendations
  }

  logSummary() {
    if (process.env.NODE_ENV === 'development') {
      const metrics = this.getMetrics()
      const recommendations = this.getRecommendations()

      console.group('📊 Bundle Analysis Summary')
      console.log(`Total Chunks: ${metrics.totalChunks}`)
      console.log(`Total Size: ${metrics.totalSize}KB`)
      console.log(`Average Load Time: ${metrics.averageLoadTime.toFixed(2)}ms`)
      
      if (metrics.largestChunk) {
        console.log(`Largest Chunk: ${metrics.largestChunk.name} (${metrics.largestChunk.size}KB)`)
      }
      
      if (metrics.slowestChunk) {
        console.log(`Slowest Chunk: ${metrics.slowestChunk.name} (${metrics.slowestChunk.loadTime.toFixed(2)}ms)`)
      }

      console.log('\n💡 Recommendations:')
      recommendations.forEach(rec => console.log(`• ${rec}`))
      console.groupEnd()
    }
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }
}

// Create singleton instance
export const bundleAnalyzer = new BundleAnalyzer()

// Auto-log summary after initial load
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    setTimeout(() => {
      bundleAnalyzer.logSummary()
    }, 2000) // Wait 2 seconds for all chunks to load
  })
}

// Feature-specific chunk tracking
export const chunkTracker = {
  trackFeatureLoad: (featureName: string) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`🚀 Feature loaded: ${featureName}`)
    }
  },

  trackRouteChange: (routeName: string) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`🔄 Route changed: ${routeName}`)
    }
  },

  trackLazyComponentLoad: (componentName: string) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`⚡ Lazy component loaded: ${componentName}`)
    }
  },
}